import React, { useEffect, useMemo, useState } from 'react';
import {
  BookText, History, RefreshCw, Save, Send, Eye, Undo2,
  AlertTriangle, CheckCircle2, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';
import { GreatSchoolsAttribution } from '../../components/DataAttribution';

// Describe FastAPI errors the way the Admin Voice Numbers card does so the
// user gets actionable messages instead of "Unknown".
function describeError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  let body = '';
  if (typeof detail === 'string') body = detail;
  else if (detail && typeof detail === 'object') {
    body = detail.message || detail.error || JSON.stringify(detail);
  } else if (e?.message) body = e.message;
  const prefix = status ? `HTTP ${status}` : 'Network error';
  return body ? `${prefix} — ${body}` : prefix;
}

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}

export default function CrmAdminAiScripts() {
  const { toast } = useToast();
  const [sections, setSections] = useState([]);          // from API
  const [draftMap, setDraftMap] = useState({});          // key → edited string
  const [activeVersion, setActiveVersion] = useState(null);
  const [draftMeta, setDraftMeta] = useState({ updated_at: null, updated_by: null });
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishNote, setPublishNote] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewFacts, setPreviewFacts] = useState(null);
  const [previewFactsSource, setPreviewFactsSource] = useState('none');
  const [previewAddress, setPreviewAddress] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: v }] = await Promise.all([
        api.get('/admin/ai-scripts/sections'),
        api.get('/admin/ai-scripts/versions'),
      ]);
      setSections(s.sections || []);
      setActiveVersion(s.active_version || null);
      setDraftMeta(s.draft || {});
      // Seed the draft map from each section's current (draft OR published).
      const seeded = {};
      (s.sections || []).forEach((row) => { seeded[row.key] = row.current; });
      setDraftMap(seeded);
      setVersions(v.versions || []);
    } catch (e) {
      toast({ title: 'Failed to load AI scripts', description: describeError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Which sections have been edited away from the DEFAULT (what the user
  // actually cares about seeing — "modified from default").
  const sectionByKey = useMemo(() => {
    const m = {};
    sections.forEach((s) => { m[s.key] = s; });
    return m;
  }, [sections]);

  const isOverridden = (key) => {
    const s = sectionByKey[key];
    if (!s) return false;
    return (draftMap[key] || '') !== s.default;
  };

  // Which sections have unsaved changes vs. the "current" the server knows about
  // (draft OR published). This drives the dirty indicator on the save button.
  const dirtyKeys = useMemo(() => {
    const out = [];
    for (const s of sections) {
      if ((draftMap[s.key] ?? '') !== s.current) out.push(s.key);
    }
    return out;
  }, [sections, draftMap]);

  const buildDraftPayload = () => {
    // Only submit sections that differ from DEFAULT — the backend ignores
    // identical-to-default values but let's keep the wire payload clean.
    const out = {};
    for (const s of sections) {
      const val = draftMap[s.key] ?? '';
      if (val !== s.default) out[s.key] = val;
    }
    return out;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = { sections: buildDraftPayload() };
      const { data } = await api.put('/admin/ai-scripts/draft', payload);
      toast({ title: 'Draft saved', description: `${data.override_count} section override${data.override_count === 1 ? '' : 's'}.` });
      await loadAll();
    } catch (e) {
      toast({ title: 'Save failed', description: describeError(e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const resetSection = async (key) => {
    if (!window.confirm(`Reset "${sectionByKey[key]?.title}" to the default?`)) return;
    try {
      await api.post('/admin/ai-scripts/draft/reset', { key });
      toast({ title: 'Section reset to default' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Reset failed', description: describeError(e), variant: 'destructive' });
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      // Save the current editor state first so publish picks it up.
      await api.put('/admin/ai-scripts/draft', { sections: buildDraftPayload() });
      await api.post('/admin/ai-scripts/publish', { note: publishNote });
      toast({ title: 'Published', description: 'New version is active for all incoming calls.' });
      setPublishOpen(false);
      setPublishNote('');
      await loadAll();
    } catch (e) {
      toast({ title: 'Publish failed', description: describeError(e), variant: 'destructive' });
    } finally { setPublishing(false); }
  };

  const rollback = async (versionId, note) => {
    if (!window.confirm(`Roll back to this version?\n\n"${note || '(no note)'}"\n\nThis will take effect on the next call.`)) return;
    try {
      await api.post(`/admin/ai-scripts/versions/${versionId}/rollback`, {});
      toast({ title: 'Rolled back', description: 'That version is now active.' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Rollback failed', description: describeError(e), variant: 'destructive' });
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    await runPreview();
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const payload = { sections: buildDraftPayload() };
      if (previewAddress.trim()) payload.sample_address = previewAddress.trim();
      const { data } = await api.post('/admin/ai-scripts/preview', payload);
      setPreviewText(data.compiled || '');
      setPreviewFacts(data.sample_neighborhood_facts || null);
      setPreviewFactsSource(data.sample_neighborhood_facts_source || 'none');
    } catch (e) {
      setPreviewText(`// preview failed — ${describeError(e)}`);
      setPreviewFacts(null);
      setPreviewFactsSource('none');
    } finally { setPreviewLoading(false); }
  };

  if (loading) {
    return (
      <Card className="p-6" data-testid="ai-scripts-loading">
        <div className="flex items-center gap-2 text-brand-green/70">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading AI receptionist scripts…
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-scripts-page">
      {/* Header card */}
      <Card className="p-5 bg-gradient-to-br from-brand-cream/40 to-white">
        <div className="flex items-start gap-3">
          <BookText className="w-6 h-6 text-brand-green mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-serif text-brand-green">AI Receptionist Scripts</h2>
            <p className="text-sm text-brand-green/70 mt-1">
              Edit each section of the system prompt the Gemini voice assistant uses on every call.
              Draft your changes, preview the compiled prompt, then publish to apply it to all live calls.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-xs">
              {activeVersion ? (
                <Badge variant="outline" className="border-brand-green/40 text-brand-green" data-testid="active-version-badge">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Active · published {fmtWhen(activeVersion.published_at)}
                  {activeVersion.published_by ? ` by ${activeVersion.published_by}` : ''}
                </Badge>
              ) : (
                <Badge variant="outline" className="border-brand-green/30 text-brand-green/70">
                  No published overrides — using defaults
                </Badge>
              )}
              {draftMeta.has_changes && (
                <Badge className="bg-brand-gold/20 text-brand-green border-brand-gold/40" data-testid="draft-badge">
                  Draft saved · {fmtWhen(draftMeta.updated_at)}
                </Badge>
              )}
              {dirtyKeys.length > 0 && (
                <Badge className="bg-amber-100 text-amber-900 border-amber-300" data-testid="unsaved-badge">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {dirtyKeys.length} unsaved section{dirtyKeys.length === 1 ? '' : 's'}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button variant="outline" onClick={openPreview} data-testid="btn-preview">
              <Eye className="w-4 h-4 mr-1" /> Preview
            </Button>
            <Button variant="outline" onClick={saveDraft} disabled={saving || dirtyKeys.length === 0} data-testid="btn-save-draft">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save draft
            </Button>
            <Button onClick={() => setPublishOpen(true)} data-testid="btn-open-publish">
              <Send className="w-4 h-4 mr-1" /> Publish
            </Button>
          </div>
        </div>
      </Card>

      {/* Section editors */}
      <div className="space-y-4">
        {sections.map((s) => {
          const value = draftMap[s.key] ?? '';
          const isDirty = dirtyKeys.includes(s.key);
          const overridden = isOverridden(s.key);
          return (
            <Card key={s.key} className="p-5" data-testid={`section-card-${s.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <h3 className="font-serif text-lg text-brand-green">{s.title}</h3>
                  <p className="text-xs text-brand-green/60 mt-0.5">{s.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {overridden && (
                    <Badge className="bg-brand-green/10 text-brand-green border-brand-green/30 text-[10px]"
                           data-testid={`badge-overridden-${s.key}`}>
                      Modified
                    </Badge>
                  )}
                  {isDirty && (
                    <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[10px]"
                           data-testid={`badge-unsaved-${s.key}`}>
                      Unsaved
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-brand-green/70 hover:text-brand-green"
                    onClick={() => setDraftMap((m) => ({ ...m, [s.key]: s.default }))}
                    disabled={value === s.default}
                    data-testid={`btn-revert-local-${s.key}`}
                    title="Load the default into the editor (not saved until you click Save draft)"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Load default
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-red-700 hover:text-red-800"
                    onClick={() => resetSection(s.key)}
                    data-testid={`btn-reset-${s.key}`}
                    title="Drop this section from the saved draft so it falls back to the default"
                  >
                    <Undo2 className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </div>
              </div>
              <Textarea
                value={value}
                onChange={(e) => setDraftMap((m) => ({ ...m, [s.key]: e.target.value }))}
                rows={Math.min(24, Math.max(6, (value.match(/\n/g) || []).length + 2))}
                className="font-mono text-xs leading-relaxed"
                data-testid={`textarea-${s.key}`}
                spellCheck={false}
              />
              <p className="text-[11px] text-brand-green/50 mt-2">
                Placeholders: <code className="font-mono">{'{agent_name}'}</code>, <code className="font-mono">{'{agent_phone}'}</code>, <code className="font-mono">{'{agent_phone_or_cell}'}</code>
                {s.key === 'language' && <> , <code className="font-mono">{'{lang_rule}'}</code></>}
                . Literal braces must be doubled (e.g. <code className="font-mono">{'{{address}}'}</code>).
              </p>
            </Card>
          );
        })}
      </div>

      {/* Version history */}
      <Card className="p-5" data-testid="version-history-card">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-5 h-5 text-brand-green" />
          <h3 className="font-serif text-lg text-brand-green">Version history</h3>
        </div>
        {versions.length === 0 ? (
          <p className="text-sm text-brand-green/60">No published versions yet — publish once to start the audit trail.</p>
        ) : (
          <div className="divide-y divide-brand-green/10">
            {versions.map((v) => (
              <div key={v.id} className="py-3 flex items-start justify-between gap-3" data-testid={`version-row-${v.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-brand-green">
                      {fmtWhen(v.published_at)}
                    </span>
                    {v.active && (
                      <Badge className="bg-brand-green text-brand-cream text-[10px]" data-testid={`badge-active-${v.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                      </Badge>
                    )}
                    <span className="text-xs text-brand-green/60">
                      {v.override_count} override{v.override_count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="text-xs text-brand-green/60 mt-1 truncate">
                    {v.published_by ? `by ${v.published_by}` : 'by unknown'}
                    {v.note ? ` — "${v.note}"` : ''}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={v.active}
                  onClick={() => rollback(v.id, v.note)}
                  data-testid={`btn-rollback-${v.id}`}
                >
                  <Undo2 className="w-3 h-3 mr-1" />
                  {v.active ? 'In use' : 'Roll back'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Publish dialog */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle>Publish AI script changes</DialogTitle>
            <DialogDescription>
              This saves your current edits and makes them active for every incoming call,
              starting with the next connection. The previous version is kept in history
              so you can roll back instantly if anything misbehaves.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="publish-note">Release note (optional)</Label>
            <Input
              id="publish-note"
              placeholder="e.g. tightened Fair-Housing step 2 wording"
              value={publishNote}
              onChange={(e) => setPublishNote(e.target.value)}
              data-testid="publish-note-input"
            />
            <p className="text-xs text-brand-green/60">
              {dirtyKeys.length > 0
                ? `${dirtyKeys.length} unsaved section${dirtyKeys.length === 1 ? ' will be saved' : 's will be saved'} and included in this release.`
                : 'All edits already match the saved draft — this will publish exactly what you see.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishOpen(false)} disabled={publishing}>Cancel</Button>
            <Button onClick={publish} disabled={publishing} data-testid="btn-confirm-publish">
              {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Publish now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Compiled prompt + sample tool output</DialogTitle>
            <DialogDescription>
              Top panel is exactly what Gemini sees at call-connect — placeholders
              filled with sample values (<code className="font-mono text-xs">Jane Realtor</code> /
              <code className="font-mono text-xs"> +1 310-555-0199</code>).
              Bottom panel shows the JSON Gemini receives when it calls the
              <code className="font-mono text-xs"> get_neighborhood_facts</code> tool
              — including the new RentCast <code className="font-mono text-xs">subject_property</code> block.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-end gap-2 mb-2">
            <div className="flex-1">
              <Label htmlFor="preview-sample-address" className="text-xs text-brand-green/70">
                Sample address for the <code className="font-mono">get_neighborhood_facts</code> tool
                (leave blank for a canned demo)
              </Label>
              <Input
                id="preview-sample-address"
                placeholder="4529 Winona Ct, Denver, CO 80212"
                value={previewAddress}
                onChange={(e) => setPreviewAddress(e.target.value)}
                data-testid="preview-sample-address-input"
              />
            </div>
            <Button variant="outline" onClick={runPreview} disabled={previewLoading}
                    data-testid="btn-rerun-preview">
              {previewLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Recompile
            </Button>
          </div>

          <div className="rounded-md border bg-brand-cream/40 p-3 max-h-[40vh] overflow-auto mb-3">
            <div className="text-[11px] uppercase tracking-wide text-brand-green/60 mb-1">System prompt</div>
            {previewLoading ? (
              <div className="flex items-center gap-2 text-brand-green/70">
                <Loader2 className="w-4 h-4 animate-spin" /> Compiling preview…
              </div>
            ) : (
              <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-brand-green"
                   data-testid="preview-text">
                {previewText}
              </pre>
            )}
          </div>

          <div className="rounded-md border bg-white p-3 max-h-[30vh] overflow-auto">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wide text-brand-green/60">
                Tool output: <code className="font-mono normal-case">get_neighborhood_facts</code>
              </div>
              <Badge className={previewFactsSource === 'live'
                ? 'bg-brand-green/15 text-brand-green border-brand-green/30 text-[10px]'
                : 'bg-brand-cream text-brand-green/70 border-brand-green/20 text-[10px]'}
                     data-testid="preview-facts-source-badge">
                {previewFactsSource === 'live' ? 'live from RentCast + GreatSchools' : 'demo payload'}
              </Badge>
            </div>
            {previewFacts ? (
              <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-brand-green"
                   data-testid="preview-facts-json">
                {JSON.stringify(previewFacts, null, 2)}
              </pre>
            ) : (
              <p className="text-xs text-brand-green/60">No sample loaded.</p>
            )}
          </div>

          {/* GreatSchools attribution — tool output surfaces school data,
              so this dialog counts as a "page/view" per the 2024-12 policy.
              `showRatingScale` lights up only when the preview payload
              actually contains a numeric rating; on lower GreatSchools
              tiers the sentence stays off to avoid implying otherwise. */}
          <GreatSchoolsAttribution
            className="mt-2 border-t border-brand-green/10 pt-2"
            compact
            showRatingScale={Array.isArray(previewFacts?.schools)
              && previewFacts.schools.some((s) => typeof s?.rating === 'number'
                                                   && s.rating >= 1 && s.rating <= 10)}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
